import { CircularProgress, Dialog, Tab, Tabs } from "@mui/material";
import Button from "components/common/Button";
import ModificationSelection from "components/common/ModificationSelection";
import api from "config/api";
import { useAppSelector } from "hooks/store";
import { useEffect, useMemo, useRef, useState } from "react";
import { convertNumberToHex } from "screens";
import { ModifierItemType } from "utils/types";
import { flushSync } from "react-dom";

type Props = {
  open: any;
  setOpen: (v: any) => void;
  onSubmit: (v: any) => void;
};

const ModificationPopUp = ({ open, setOpen, onSubmit }: Props) => {
  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState<ModifierItemType[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [config, setConfig] = useState({});
  const [defaultToppings, setDefaulToppings] = useState<any>({});
  const configRef = useRef<any>({});
  const { settings } = useAppSelector(({ main }) => main);

  const updateConfig = (updater: any) => {
    setConfig((prev: any) => {
      const next =
        typeof updater === "function"
          ? updater(prev)
          : {
              ...prev,
              ...updater,
            };
      configRef.current = next;
      return next;
    });
  };

  const buildCategoryConfig = (category: any, savedModifiers: any[] = []) => {
    const savedMap = new Map(
      (savedModifiers || []).map((item: any) => [
        Number(item?.pizzaModifierId || item?.modifierId),
        item,
      ]),
    );

    return (category?.modifiers || []).map((modifier: any) => {
      const saved = savedMap.get(Number(modifier?.pizzaModifierId));
      const selected =
        typeof saved?.selected === "boolean"
          ? saved.selected
          : saved
          ? true
          : typeof modifier?.selected === "boolean"
          ? modifier.selected
          : Number(modifier?.defaultTopping) === 1;

      return {
        ...modifier,
        ...saved,
        selected,
      };
    });
  };

  useEffect(() => {
    if (open) {
      setLoading(true);
      api
        .get(`/api/Modifier/ModifierCategories?${new URLSearchParams(open)}`)
        .then(({ data }) => {
          setSelectedTab(data[0].modifierId);
          setTabs(data);
          const temp: any = {};
          const list = data.reduce((obj: any, itm: any) => {
            const savedCategory = open?.modifications?.[itm.modifierId] || [];
            temp[itm.modifierId] = itm.modifiers.filter(
              (i: any) => Number(i.defaultTopping) === 1,
            ).length;

            return {
              ...obj,
              [itm.modifierId]: buildCategoryConfig(itm, savedCategory),
            };
          }, {});

          setConfig(list);
          configRef.current = list;
          setDefaulToppings(temp);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  const selectOptions = useMemo(
    () => tabs.find((i) => i.modifierId === selectedTab)?.modifiers || [],
    [tabs, selectedTab],
  );

  return (
    <Dialog
      onClose={() => setOpen(undefined)}
      open={!!open}
      maxWidth="lg"
      PaperProps={{
        className: "rounded-[24px] overflow-hidden",
      }}
    >
      <div className="min-w-[80vw] md:min-w-[600px] bg-[#fcfcfa]">
        <div className="border-b border-black/10 px-4 pt-5 pb-4 sm:px-5">
          <h4 className="!text-lg text-[#222] font-bold">
            Customize Your Order
          </h4>
          <p className="mt-1 text-[13px] text-gray-500">
            Pick the extras you want before saving.
          </p>
        </div>
        {!!tabs.length && (
          <div className="px-2 sm:px-3 border-b border-black/10 bg-white">
            <Tabs
              value={selectedTab}
              onChange={(_, v) => setSelectedTab(v)}
              aria-label="basic tabs example"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 50,
                "& .MuiTab-root": {
                  minHeight: 50,
                  textTransform: "none",
                  fontWeight: 600,
                },
                "& .Mui-selected": {
                  color: `${convertNumberToHex(
                    settings.themeColor,
                  )} !important`,
                },
              }}
            >
              {tabs.map((i) => (
                <Tab value={i.modifierId} label={i.modifierName} />
              ))}
            </Tabs>
          </div>
        )}

        <div className="h-[60vh] flex flex-col">
          {loading ? (
            <CircularProgress className="self-center mt-[10%]" />
          ) : (
            <ModificationSelection
              label="pizzaModifierName"
              selectedTab={selectedTab}
              config={config}
              setConfig={updateConfig}
              options={selectOptions}
            />
          )}
        </div>
        <div className="flex justify-between gap-3 p-4 border-t border-black/10 bg-white">
          <Button className="!rounded-[16px] !px-5" onClick={() => setOpen(undefined)}>
            Cancel
          </Button>
          <Button
            className="!rounded-[16px] !px-6"
            onClick={() => {
              flushSync(() => {});
              const latestConfig = configRef.current;
              onSubmit(
                Object.entries(latestConfig).reduce((obj: any, [key, arr]: any) => {
                  const array = structuredClone(arr || []);
                  const selectedItems = array.filter((value: any) => value.selected);
                  const freeLimit = Number(defaultToppings[key] || 0);
                  const freeIds = new Set(
                    selectedItems
                      .filter((value: any) => Number(value.pizzaModifierPrice || 0) > 0)
                      .sort(
                        (a: any, b: any) =>
                          Number(a.pizzaModifierPrice || 0) -
                            Number(b.pizzaModifierPrice || 0) ||
                          Number(a.pizzaModifierId || 0) -
                            Number(b.pizzaModifierId || 0),
                      )
                      .slice(0, freeLimit)
                      .map((value: any) => Number(value.pizzaModifierId)),
                  );

                  obj[key] = array.map((value: any) => {
                    const price = Number(value?.pizzaModifierPrice || 0);
                    const isSelected = !!value?.selected;
                    const getsFreeSlot =
                      isSelected && price > 0 && freeIds.has(Number(value.pizzaModifierId));

                    if (!value.defaultTopping && getsFreeSlot) {
                      value.newDefaultTopping = 1;
                    } else {
                      delete value.newDefaultTopping;
                    }
                    return value;
                  });
                  return obj;
                }, {}),
              );
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ModificationPopUp;
